import { JsonRpcProvider, Transaction, Wallet, getAddress } from 'ethers';
import type { Web3Auth } from '@web3auth/modal';
import type { IProvider } from '@web3auth/base';
import type { ChainId } from '@cryptopay/core';
import { getBrowserChain } from './chains.js';

// Sapphire network identifier (matches WEB3AUTH_NETWORK constants).
const SAPPHIRE_DEVNET = 'sapphire_devnet' as const;
type Web3AuthNetwork = 'sapphire_devnet' | 'sapphire_mainnet';

export interface AuthSession {
  readonly email: string;
  readonly address: string;
  readonly idToken: string;
  readonly provider: 'web3auth' | 'dev-wallet';
}

export interface UnsignedTransfer {
  readonly chainId: ChainId;
  readonly to: string;
  readonly data: string;
  readonly gasLimit?: bigint;
}

export interface AuthClient {
  readonly mode: 'web3auth' | 'dev-wallet';
  login(email: string): Promise<AuthSession>;
  signTransfer(tx: UnsignedTransfer): Promise<string>;
  logout(): Promise<void>;
  /**
   * Resumes a session after a full-page redirect (iOS Safari path).
   * Returns null when there is nothing to resume — callers should
   * fall through to the Login screen in that case.
   */
  restoreSession(): Promise<AuthSession | null>;
}

/**
 * Local-development fallback when no Web3Auth client ID is configured.
 * Generates a deterministic-per-email wallet in browser memory only.
 * Never used in production: the App layer enforces this gate and shows
 * a dev-mode banner.
 */
export class DevWalletAuthClient implements AuthClient {
  readonly mode = 'dev-wallet' as const;
  private current: { wallet: Wallet; session: AuthSession } | null = null;

  async login(email: string): Promise<AuthSession> {
    if (!email || !email.includes('@')) {
      throw new Error('DevWalletAuthClient.login: valid email required');
    }
    const seed = await sha256(`dev-wallet:${email.toLowerCase()}`);
    const wallet = new Wallet(seed);
    const session: AuthSession = {
      email: email.toLowerCase(),
      address: wallet.address,
      idToken: makeFakeIdToken(email, wallet.address),
      provider: 'dev-wallet',
    };
    this.current = { wallet, session };
    return session;
  }

  async signTransfer(tx: UnsignedTransfer): Promise<string> {
    if (!this.current) throw new Error('DevWalletAuthClient: login first');
    const chain = getBrowserChain(tx.chainId);
    const provider = new JsonRpcProvider(chain.rpcUrl);
    const connected = this.current.wallet.connect(provider);
    const nonce = await provider.getTransactionCount(this.current.wallet.address);
    const fees = await provider.getFeeData();
    const populated = Transaction.from({
      to: tx.to,
      data: tx.data,
      nonce,
      gasLimit: tx.gasLimit ?? 80_000n,
      maxFeePerGas: fees.maxFeePerGas ?? 30_000_000_000n,
      maxPriorityFeePerGas: fees.maxPriorityFeePerGas ?? 1_000_000_000n,
      chainId: chain.evmChainId,
      value: 0n,
      type: 2,
    });
    return connected.signTransaction(populated);
  }

  async logout(): Promise<void> {
    this.current = null;
  }

  async restoreSession(): Promise<AuthSession | null> {
    return null;
  }
}

/**
 * Web3Auth (Sapphire) production adapter.
 *
 * 2-of-3 MPC: share'ler kullanıcı email auth + cihaz + Web3Auth network'te.
 * Sunucu hiçbir share'i tutmaz; key sadece browser memory'de yeniden
 * birleştirilip imzalama anında kullanılır.
 *
 * Flow:
 * 1. initModal() → openlogin adapter ile email_passwordless yapılandırılır
 * 2. connect() → kullanıcı email OTP girer, MPC share'leri birleşir
 * 3. authenticateUser() → backend'e gönderilecek JWT (JWKS doğrulamalı)
 * 4. eth_signTransaction → imza, broadcast değil (backend tx-relay broadcast eder)
 */
export interface Web3AuthClientOptions {
  readonly clientId: string;
  readonly network?: Web3AuthNetwork;
  readonly defaultChainId?: ChainId;
}

export class Web3AuthClient implements AuthClient {
  readonly mode = 'web3auth' as const;
  private web3auth: Web3Auth | null = null;
  private provider: IProvider | null = null;
  private readonly options: Required<Web3AuthClientOptions>;

  constructor(options: Web3AuthClientOptions) {
    if (!options.clientId) {
      throw new Error('Web3AuthClient: clientId is required');
    }
    this.options = {
      clientId: options.clientId,
      network: options.network ?? SAPPHIRE_DEVNET,
      defaultChainId: options.defaultChainId ?? 'polygon',
    };
  }

  private async init(): Promise<Web3Auth> {
    if (this.web3auth) return this.web3auth;
    // Lazy-load Web3Auth SDK: keeps the bundle small until login is invoked
    // and avoids running the SDK's module-level crypto in non-browser
    // test environments (jsdom lacks some browser globals).
    const [{ Web3Auth: Web3AuthCtor }, baseMod, providerMod] = await Promise.all([
      import('@web3auth/modal'),
      import('@web3auth/base'),
      import('@web3auth/ethereum-provider'),
    ]);
    const chain = getBrowserChain(this.options.defaultChainId);
    const chainConfig = {
      chainNamespace: baseMod.CHAIN_NAMESPACES.EIP155,
      chainId: '0x' + chain.evmChainId.toString(16),
      rpcTarget: chain.rpcUrl,
      displayName: chain.name,
      blockExplorerUrl: chain.blockExplorer,
      ticker: chain.nativeSymbol,
      tickerName: chain.nativeSymbol,
    } as const;
    const privateKeyProvider = new providerMod.EthereumPrivateKeyProvider({
      config: { chainConfig },
    });
    const web3auth = new Web3AuthCtor({
      clientId: this.options.clientId,
      web3AuthNetwork: this.options.network as never,
      privateKeyProvider,
    });
    await web3auth.initModal();
    this.web3auth = web3auth;
    return web3auth;
  }

  async restoreSession(): Promise<AuthSession | null> {
    // Popup uxMode keeps state inside the SDK across page nav, but a hard
    // reload drops it. Try to read whatever the SDK has after initModal()
    // — if nothing, return null and fall back to the Login screen.
    try {
      const web3auth = await this.init();
      if (!web3auth.connected) return null;
      this.provider = web3auth.provider;
      if (!this.provider) return null;
      const { idToken } = await web3auth.authenticateUser();
      if (!idToken) return null;
      const info = await web3auth.getUserInfo();
      const accounts = (await this.provider.request({ method: 'eth_accounts' })) as string[];
      const address = accounts[0];
      if (!address) return null;
      return {
        email: (info.email ?? '').toLowerCase(),
        address: getAddress(address),
        idToken,
        provider: 'web3auth',
      };
    } catch {
      return null;
    }
  }

  async login(email: string): Promise<AuthSession> {
    const web3auth = await this.init();
    if (!web3auth.connected) {
      const { WALLET_ADAPTERS } = await import('@web3auth/base');
      const hintedEmail = email && email.includes('@') ? email : undefined;
      try {
        this.provider = (await web3auth.connectTo(WALLET_ADAPTERS.AUTH, {
          loginProvider: 'email_passwordless',
          extraLoginOptions: hintedEmail ? { login_hint: hintedEmail } : {},
        })) as IProvider;
      } catch {
        this.provider = (await web3auth.connect()) as IProvider;
      }
    } else {
      this.provider = web3auth.provider;
    }
    if (!this.provider) {
      throw new Error('Web3Auth: provider unavailable after login');
    }

    const { idToken } = await web3auth.authenticateUser();
    if (!idToken) throw new Error('Web3Auth: no idToken from authenticateUser');

    const info = await web3auth.getUserInfo();
    const accounts = (await this.provider.request({ method: 'eth_accounts' })) as string[];
    const address = accounts[0];
    if (!address) throw new Error('Web3Auth: no EVM account returned');

    return {
      email: (info.email ?? email).toLowerCase(),
      address: getAddress(address),
      idToken,
      provider: 'web3auth',
    };
  }

  async signTransfer(tx: UnsignedTransfer): Promise<string> {
    if (!this.provider) throw new Error('Web3AuthClient: login first');
    const chain = getBrowserChain(tx.chainId);
    const rpc = new JsonRpcProvider(chain.rpcUrl);
    const accounts = (await this.provider.request({ method: 'eth_accounts' })) as string[];
    const from = accounts[0];
    if (!from) throw new Error('Web3AuthClient: no signer account');
    const nonce = await rpc.getTransactionCount(from);
    const fees = await rpc.getFeeData();
    const gasLimit = tx.gasLimit ?? 80_000n;
    const maxFeePerGas = fees.maxFeePerGas ?? 30_000_000_000n;
    const maxPriorityFeePerGas = fees.maxPriorityFeePerGas ?? 1_000_000_000n;

    const unsignedTx = {
      from,
      to: tx.to,
      data: tx.data,
      nonce: '0x' + nonce.toString(16),
      gas: '0x' + gasLimit.toString(16),
      maxFeePerGas: '0x' + maxFeePerGas.toString(16),
      maxPriorityFeePerGas: '0x' + maxPriorityFeePerGas.toString(16),
      chainId: '0x' + chain.evmChainId.toString(16),
      value: '0x0',
      type: '0x2',
    };

    const signed = (await this.provider.request({
      method: 'eth_signTransaction',
      params: [unsignedTx],
    })) as string;
    return signed;
  }

  async logout(): Promise<void> {
    if (this.web3auth?.connected) {
      await this.web3auth.logout();
    }
    this.provider = null;
  }
}

export interface AuthFactoryEnv {
  readonly clientId: string;
  readonly mode: 'production' | 'development' | 'test';
  /**
   * Set via `VITE_FORCE_DEV_WALLET=true` to force the deterministic
   * email-derived wallet path even in production builds. Use when
   * Web3Auth onboarding is blocked (dashboard allowlist issues, MPC
   * outage) and you need an unblocked UI for staging or demo flows.
   * The DevModeBanner stays visible so this is never invisible.
   */
  readonly forceDevWallet?: boolean;
}

export function createAuthClient(env: AuthFactoryEnv): AuthClient {
  if (env.forceDevWallet) {
    return new DevWalletAuthClient();
  }
  const trimmed = env.clientId.trim();
  if (trimmed.length === 0) {
    if (env.mode === 'production') {
      throw new Error(
        'VITE_WEB3AUTH_CLIENT_ID is required in production. Configure it before deploying.',
      );
    }
    return new DevWalletAuthClient();
  }
  return new Web3AuthClient({ clientId: trimmed });
}

async function sha256(input: string): Promise<string> {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle || typeof subtle.digest !== 'function') {
    throw new Error('crypto.subtle.digest not available in this environment');
  }
  const data = new TextEncoder().encode(input);
  const buf = await subtle.digest('SHA-256', data);
  return '0x' + bufToHex(new Uint8Array(buf));
}

function bufToHex(buf: Uint8Array): string {
  let out = '';
  for (const byte of buf) out += byte.toString(16).padStart(2, '0');
  return out;
}

function makeFakeIdToken(email: string, address: string): string {
  const header = base64UrlEncode(JSON.stringify({ alg: 'none', typ: 'JWT' }));
  const payload = base64UrlEncode(
    JSON.stringify({
      iss: 'dev-wallet',
      aud: 'dev-client',
      email,
      verifier: 'dev-wallet-email',
      verifierId: email,
      wallets: [{ type: 'ethereum', address }],
      iat: Math.floor(Date.now() / 1000),
      exp: Math.floor(Date.now() / 1000) + 3600,
    }),
  );
  return `${header}.${payload}.devsig`;
}

function base64UrlEncode(input: string): string {
  if (typeof btoa === 'function') {
    return btoa(input).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }
  return Buffer.from(input, 'utf8')
    .toString('base64')
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}
